/**
 * Yellow SDK Frontend Client
 *
 * Client for interacting with Yellow SDK API from frontend applications
 * This package only contains Yellow SDK operations (channels, custody).
 * For tournament-specific operations, use @tournaments/tournament-client
 */
import axios from 'axios';
export class YellowSDKClient {
    constructor(config) {
        this.tokenCache = null;
        this.lastRequestTime = 0;
        this.MIN_REQUEST_INTERVAL = 1000; // Minimum 1 second between requests
        this.config = config;
        this.api = axios.create({
            baseURL: config.apiUrl,
        });
        // Add request interceptor to include Privy token with throttling
        this.api.interceptors.request.use(async (config) => {
            // Throttle requests - ensure minimum interval between requests
            const now = Date.now();
            const timeSinceLastRequest = now - this.lastRequestTime;
            if (timeSinceLastRequest < this.MIN_REQUEST_INTERVAL) {
                await new Promise(resolve => setTimeout(resolve, this.MIN_REQUEST_INTERVAL - timeSinceLastRequest));
            }
            this.lastRequestTime = Date.now();
            if (this.config.getAccessToken) {
                try {
                    // Cache token for 30 seconds to reduce calls to Privy
                    const now = Date.now();
                    if (this.tokenCache && this.tokenCache.expiresAt > now) {
                        if (this.tokenCache.token) {
                            config.headers.Authorization = `Bearer ${this.tokenCache.token}`;
                        }
                        return config;
                    }
                    const token = await this.config.getAccessToken();
                    // Cache token for 30 seconds
                    this.tokenCache = {
                        token,
                        expiresAt: now + 30000, // 30 seconds
                    };
                    if (token) {
                        config.headers.Authorization = `Bearer ${token}`;
                    }
                    else {
                        console.warn('[YellowSDKClient] getAccessToken returned null/undefined');
                    }
                }
                catch (error) {
                    console.error('[YellowSDKClient] Error getting access token:', error);
                    // Clear cache on error
                    this.tokenCache = null;
                }
            }
            return config;
        });
        // Add response interceptor to handle 429 errors
        this.api.interceptors.response.use((response) => response, async (error) => {
            if (error?.response?.status === 429) {
                // Rate limited - wait and retry once
                const retryAfter = error.response.headers['retry-after']
                    ? parseInt(error.response.headers['retry-after']) * 1000
                    : 5000; // Default 5 seconds
                console.warn(`[YellowSDKClient] Rate limited (429), waiting ${retryAfter}ms before retry`);
                await new Promise(resolve => setTimeout(resolve, retryAfter));
                // Clear token cache to force fresh token
                this.tokenCache = null;
                // Retry the request
                return this.api.request(error.config);
            }
            return Promise.reject(error);
        });
    }
    // Yellow SDK methods (custody and channels only)
    async getCustodyBalance(address) {
        const endpoint = address ? `/custody/balance/${address}` : '/custody/balance';
        const response = await this.api.get(endpoint);
        return response.data.balance || response.data.custodyBalance?.wei || '0';
    }
    async getAllCustodyBalances() {
        const response = await this.api.get('/custody/balances');
        return response.data.balances || [];
    }
    async getChannelState() {
        try {
            const response = await this.api.get('/custody/diagnostic');
            const diagnostics = response.data.diagnostics || {};
            const channelInfo = diagnostics.channelInfo || {};
            return {
                channelId: channelInfo.channelId || diagnostics.channelId || null,
                version: channelInfo.version ? BigInt(channelInfo.version) : null,
                custodyBalance: diagnostics.custodyBalance || '0',
                tokenName: diagnostics.config?.tokenAddress || 'Unknown',
                tokenDecimals: 6, // Default for USDC
            };
        }
        catch (error) {
            // If 503 (Yellow SDK not connected), return a default state instead of throwing
            if (error?.response?.status === 503 || error?.response?.statusText === 'Service Unavailable') {
                throw {
                    ...error,
                    message: 'Yellow SDK not connected',
                    response: { status: 503, statusText: 'Service Unavailable' },
                };
            }
            throw error;
        }
    }
    // Health check
    async healthCheck() {
        const response = await this.api.get('/health');
        return response.data;
    }
}
